import { useCallback, useEffect, useRef } from 'react';
import { createLiveChannel } from '../utils/live/liveChannel';

/*
 * A live channel (utils/live/liveChannel.js) for as long as a component shows
 * it: `pull` and `onData` may change on every render without restarting it;
 * `enabled` false (or a new `key`) stops it. `onError(error, failures)` hears
 * of a failed pull. Returns `poke()`: ask now.
 */
export default function useLiveChannel(pull, onData, { enabled = true, base, max, key = '', onError } = {}) {
  const pullRef = useRef(pull);
  const dataRef = useRef(onData);
  const errorRef = useRef(onError);
  pullRef.current = pull;
  dataRef.current = onData;
  errorRef.current = onError;
  const channel = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    const live = createLiveChannel({
      pull: () => pullRef.current(),
      onData: (data) => dataRef.current?.(data),
      onError: (error, failures) => errorRef.current?.(error, failures),
      base,
      max,
    });
    channel.current = live;
    live.start();
    return () => {
      live.stop();
      if (channel.current === live) channel.current = null;
    };
  }, [enabled, base, max, key]);

  return useCallback(() => channel.current?.poke(), []);
}
